/**
 * Aging-colour util shared by the lane bodies (Tasks Rebuild Task 9 —
 * Client lane; reused by Task 10's Lender lane per the brief).
 *
 * Thresholds per the validated design (spec.md Part 4): green <2 days
 * waiting, amber 2-5 days, red >5 days.
 *
 * `oldestReceived`/`received_date` values returned by the /ui/lanes/*
 * endpoints are server-generated from Python's `datetime.utcnow()` and
 * serialised with `.isoformat()`, which does NOT append a `Z` or offset
 * suffix (e.g. "2026-07-14T10:30:00"). Plain `new Date(...)` parses a
 * suffix-less string as *browser-local* time, not UTC, which is wrong by
 * a full hour during BST and can flip an aging bucket near a 2d/5d
 * boundary. `parseBackendTimestamp` below appends `Z` to any timestamp
 * that has no timezone designator before parsing, so every date in this
 * file is treated as the UTC instant it actually is. This IS effectively
 * the ambiguous-date-parsing gotcha documented elsewhere in this app for
 * Proclaim/Cosmos UK DD/MM/YYYY strings, just a different flavour of it
 * (missing offset rather than ambiguous day/month order).
 */

export type AgingLevel = 'green' | 'amber' | 'red';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Matches a trailing `Z`/`z`, or a `+HH:MM`/`-HH:MM`/`+HHMM`/`-HHMM`
 * offset, i.e. any ISO-8601 timezone designator. */
const TZ_SUFFIX_RE = /(?:Z|z|[+-]\d{2}:?\d{2})$/;

/**
 * Parses a backend timestamp string as UTC when it has no timezone
 * designator (the naive `datetime.utcnow().isoformat()` shape emitted by
 * the /ui/lanes/* endpoints), leaving already-suffixed strings untouched.
 * Exported for reuse by Task 10's Lender lane and any other caller that
 * consumes these backend timestamps.
 */
export function parseBackendTimestamp(iso: string): Date {
  const withOffset = TZ_SUFFIX_RE.test(iso) ? iso : `${iso}Z`;
  return new Date(withOffset);
}

/** Fractional days between an ISO date string and `now`. `null` when the
 * input is missing or unparseable. */
export function daysSince(isoDate: string | null | undefined, now: Date = new Date()): number | null {
  if (!isoDate) return null;
  const parsed = parseBackendTimestamp(isoDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return (now.getTime() - parsed.getTime()) / DAY_MS;
}

/** Aging bucket for an ISO date string, or `null` when it can't be
 * determined (caller should render a neutral/unknown state). */
export function agingLevel(isoDate: string | null | undefined, now: Date = new Date()): AgingLevel | null {
  const days = daysSince(isoDate, now);
  if (days === null) return null;
  if (days < 2) return 'green';
  if (days <= 5) return 'amber';
  return 'red';
}

/** Short human label ("<1d" / "3d" / "12d") for the waiting duration,
 * "—" when unknown. */
export function agingLabel(isoDate: string | null | undefined, now: Date = new Date()): string {
  const days = daysSince(isoDate, now);
  if (days === null) return '—';
  if (days < 1) return '<1d';
  return `${Math.floor(days)}d`;
}

/** Compact "DD/MM/YY" display for completed/archived list-row attribution
 * (demo finding: "for completed emails we're missing a lot of the details
 * of who completed it and when it was completed"). Unlike each lane's own
 * day/month-only formatter (formatReceivedDDMM/formatDDMM -- appropriate
 * for a "how fresh is this" received/created date), a completed/archived
 * date can be arbitrarily old, so the year is included to disambiguate.
 * Shared across ClientLane/LenderLane/ManualLane rather than each
 * re-implementing it, same idiom as `parseBackendTimestamp` itself. */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseBackendTimestamp(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}
